/**
 * Sharing ONE layer, not the whole application.
 *
 * The layer-level sibling of `ShareModuleDialog` — same shape, since the
 * decision an owner is making ("which of these people can see this") is the
 * same one — but reachable by the layer's OWNER directly rather than
 * admin-only. See `LayerShare`'s own docstring on the server for why a layer
 * is deliberately more permissive than a whole module: handing over an entire
 * application just so one boundary layer can be reused elsewhere is a much
 * bigger grant than the person asking usually wants to make.
 *
 * A recipient gets the layer copied into whatever map they build with it —
 * see `layerLibraryService.fetchLayer` — never a live link back to this one.
 */
import { useCallback, useEffect, useState } from 'react';
import { Building2, Check, Share2, UsersRound, X } from 'lucide-react';
import type { Group, User } from '../../types';
import { userService } from '../../services/userService';
import { groupService } from '../../services/groupService';
import { layerLibraryService } from '../../services/layerLibraryService';

export default function LayerShareDialog({
  moduleId,
  layerId,
  layerName,
  onClose,
}: {
  moduleId: string;
  layerId: string;
  layerName: string;
  onClose: () => void;
}) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [sharedWithOrg, setSharedWithOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // `groupService.list()` is open to any signed-in user (Settings ›
      // User Groups is now read-only-visible to everyone) — picking an
      // EXISTING group to share this layer with needs no `groups:manage`.
      const [all, shared, allGroups] = await Promise.all([
        userService.listUsers(),
        layerLibraryService.fetchLayerShare(moduleId, layerId),
        groupService.list(),
      ]);
      // Admins can already read every layer — sharing one with them changes
      // nothing, the same reasoning ShareModuleDialog applies to modules.
      setUsers(all.filter((user) => user.role !== 'admin'));
      setSelectedIds(shared.userIds);
      setSharedWithOrg(shared.sharedWithOrg);
      setGroups(allGroups);
      setSelectedGroupIds(shared.groupIds);
    } catch {
      setError('Could not load sharing for this layer.');
    } finally {
      setLoading(false);
    }
  }, [moduleId, layerId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const toggle = (userId: string) =>
    setSelectedIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );

  const toggleGroup = (groupId: string) =>
    setSelectedGroupIds((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await layerLibraryService.setLayerShare(moduleId, layerId, selectedIds, sharedWithOrg, selectedGroupIds);
      onClose();
    } catch {
      setError('Could not update sharing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="gl-modal-backdrop fixed inset-0 z-[9500] flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Share ${layerName}`}
        className="gl-modal w-full max-w-md overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#0D9488]">
              <Share2 className="h-3.5 w-3.5" />
              Share layer
            </p>
            <h3 className="mt-0.5 truncate text-base font-semibold text-[#0F2942]">{layerName}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
            Selected users can add a <strong className="font-semibold text-slate-700">copy</strong> of this
            layer to their own maps, from Add layers &rsaquo; Existing layer. They do not get access to
            this application, and a later change here does not affect a copy already made.
          </p>

          <button
            type="button"
            onClick={() => setSharedWithOrg((current) => !current)}
            disabled={loading}
            className={`mb-3 flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition disabled:opacity-50 ${
              sharedWithOrg ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <span
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                sharedWithOrg ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300 bg-white'
              }`}
            >
              {sharedWithOrg && <Check className="h-3 w-3 text-white" />}
            </span>
            <Building2 className="h-4 w-4 flex-shrink-0 text-indigo-500" />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-[#0F2942]">Share with Organization</span>
              <span className="block text-[11px] text-slate-400">
                Every signed-in user can see this layer — view only, in Settings &rsaquo; Organization.
              </span>
            </span>
          </button>

          {!loading && groups.length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <UsersRound className="h-3.5 w-3.5" />
                Share with a group
              </p>
              <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                {groups.map((group) => {
                  const selected = selectedGroupIds.includes(group.id);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${
                        selected ? 'bg-teal-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                          selected ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#0F2942]">
                        {group.name}
                      </span>
                      <span className="flex-shrink-0 text-[10.5px] text-slate-400">
                        {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Loading users…</p>
          ) : users.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-xs text-slate-400">
              No non-admin users exist yet. Create one in Settings → User Management.
            </p>
          ) : (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {users.map((user) => {
                const selected = selectedIds.includes(user.id);
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => toggle(user.id)}
                    className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${
                      selected ? 'bg-teal-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                        selected ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {selected && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-[#0F2942]">{user.name}</span>
                      <span className="block truncate text-[11px] text-slate-400">{user.email}</span>
                    </span>
                    {user.status === 'disabled' && (
                      <span className="flex-shrink-0 text-[10px] uppercase text-slate-400">disabled</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-[11px] text-slate-400">
            {selectedIds.length} {selectedIds.length === 1 ? 'user' : 'users'}
            {selectedGroupIds.length > 0
              ? `, ${selectedGroupIds.length} group${selectedGroupIds.length === 1 ? '' : 's'}`
              : ''}
            {sharedWithOrg ? ', organization' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || loading}
              className="rounded-lg bg-[#0F2942] px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save sharing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
